import { useState, useCallback, useEffect } from 'react';
import { get_deployment } from '../../../../API';
import { useChannel } from '../../../../Realtime';
import { useInterval } from '../../../../Util';

/**
 * User presence data for facepile
 */
export interface UserPresence {
  name: string;
  lastSeen: number;
  unsavedChanges?: boolean;
  browserTab?: string;
}

/**
 * Hook to track other users viewing the same case (facepile functionality)
 * @param caseId - The support case ID
 * @param currentUserName - The current user's name
 * @returns Array of other users currently viewing the case
 */
export const useOtherUsersViewing = (caseId: string, currentUserName: string) => {
  const [userList, setUserList] = useState<Record<string, UserPresence>>({});

  const channelName = get_deployment() + ':comms:' + caseId;

  const facePileCallback = useCallback((data: any) => {
    if (data.event === "heartbeat") {
      const userKey = data.data.name + '/' + data.data.browserTab;
      setUserList((lastSeen) => ({
        ...lastSeen,
        [userKey]: {
          lastSeen: Date.now(),
          ...data.data
        }
      }));
    }

    if (data.event === "remove_user") {
      const userKey = data.data.name + '/' + data.data.browserTab;
      setUserList((lastSeen) => {
        const newUsers = { ...lastSeen };
        delete newUsers[userKey];
        return newUsers;
      });
    }
  }, []);

  const sendData = useChannel({
    realm: 'facepile',
    name: channelName,
    onMessage: facePileCallback,
  });

  useEffect(() => {
    const handleBeforeUnload = () => {
      sendData({
        realm: 'facepile',
        channel: get_deployment() + ':comms:' + caseId,
        event: 'remove_user',
        data: {
          name: currentUserName,
          browserTab: 'Support Case'
        }
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendData, currentUserName, caseId]);

  useInterval(() => {
    if (currentUserName && document.visibilityState === "visible") {
      sendData({
        realm: 'facepile',
        channel: channelName,
        event: 'heartbeat',
        data: {
          name: currentUserName,
          unsavedChanges: false,
          browserTab: 'Support Case'
        }
      });
    }
  }, 1000);

  // Show all users (including same user from other tabs) - matching old FacePile behavior
  const allUserKeys = Object.keys(userList);
  const now = Date.now();

  const otherUsers = allUserKeys
    .filter(k => {
      const timeSinceLastSeen = now - userList[k].lastSeen;
      const isActive = timeSinceLastSeen < 20000;
      return isActive; // Only filter by activity, not by user identity
    })
    .map((k) => userList[k]);

  return otherUsers;
};
